import { Network } from '@spritz-finance/sdk'
import { Body, Collapsible, CollapsibleMethods, colors, Margin, Spacer } from '@spritz-finance/ui'
import styled from 'styled-components'
import { Row } from '../../../../../components/Layout/Row'
import { formatCurrency } from '../../../../../utils/format'
import { NetworkLogo } from '../../../../../web3/networkSwitcher/NetworkLogo'
import { maskedAddress } from '../../../../../web3/utils/maskedAddress'
import { useWeb3Connection } from '../../../../../web3/Web3Provider'
import { SubscriptionData } from './SubscriptionForm'
import { getDateFromDay, getOrdinal } from './utils'
import usdcLogo from '../../../../../assets/img/usdc.svg'
import { useRef, useState } from 'react'
import { cssVar } from '../../../../../theme/utils'
import { ExpandDetailIcon } from '../../../../../components/ExpandedDetailIcon'
import { SubscriptionAccount } from './create/CreateSubscriptionButton'

const Logo = styled.img`
    width: 24px;
`

const BreakdownContainer = styled.div`
    border-top: 1px solid var(--text-secondary, ${colors.ltGrey});
    border-bottom: 1px solid var(--text-secondary, ${colors.ltGrey});
    padding: 0 ${Margin.Small};
`

const MultilineContainer = styled.div`
    display: flex;
    flex-direction: column;
`

const ClickableRow = styled.div`
    display: flex;
    flex: 1;
    justify-content: space-between;
    &:hover {
        cursor: pointer;
    }
`

const CaretWrapper = styled.div`
    display: flex;
    align-items: center;
`

const useSubscriptionDetails = (account: SubscriptionAccount, data: SubscriptionData) => {
    const { account: address } = useWeb3Connection()
    const firstPayment = getDateFromDay(data.day)

    const amount = Number(data.amount)
    const fee = Math.round((amount * 0.0195 + Number.EPSILON) * 100) / 100
    const total = amount + fee

    return {
        accountName: account.name,
        wallet: maskedAddress(address),
        ordinalDay: `${data.day}${getOrdinal(Number(data.day))}`,
        amount: formatCurrency(amount),
        fee: formatCurrency(fee),
        total: formatCurrency(total),
        firstPaymentDate: firstPayment.format('MMM DD, YYYY'),
        totalPayments: Number(data.totalPayments) - 1,
    }
}

export const SubscriptionDetails = ({
    account,
    data,
}: {
    account: SubscriptionAccount
    data: SubscriptionData
}) => {
    const defaultExpanded = false
    const [expanded, setExpanded] = useState(defaultExpanded)
    const priceBreakdown = useRef<CollapsibleMethods>(null)
    const { totalPayments, firstPaymentDate, ordinalDay, accountName, amount, wallet, fee, total } =
        useSubscriptionDetails(account, data)

    const toggle = () => {
        setExpanded((curr) => !curr)
        priceBreakdown.current?.toggle()
    }

    return (
        <>
            <Row align="space-between">
                <Body.Med size={16}>Pay to:</Body.Med>
                <Body.SmBold size={16}>{accountName}</Body.SmBold>
            </Row>
            <Spacer height={8} />
            <Spacer height={4} />
            <Row align="space-between">
                <ClickableRow onClick={() => toggle()}>
                    <Body.Med size={16}>Recurring payment amount:</Body.Med>
                    <CaretWrapper>
                        <Body.SmBold>{amount}</Body.SmBold>
                        <ExpandDetailIcon
                            size={24}
                            active={expanded}
                            tint={cssVar('--text-secondary', colors.charcoal)}
                        />
                    </CaretWrapper>
                </ClickableRow>
            </Row>
            <Collapsible ref={priceBreakdown} defaultOpen={defaultExpanded}>
                <Spacer height={16} />
                <BreakdownContainer>
                    <Spacer height={8} />
                    <Row align="space-between" style={{ alignItems: 'flex-start' }}>
                        <MultilineContainer>
                            <Body.Med size={14}>Estimated Spritz fee:</Body.Med>
                            <Body.Med
                                size={14}
                                color={cssVar('--text-tertiery', colors.medGrey)}
                                style={{ fontStyle: 'italic', maxWidth: 500 }}
                            >
                                Calculated based on monthly volume
                            </Body.Med>
                        </MultilineContainer>
                        <Body.Med size={14}>{fee}</Body.Med>
                    </Row>
                    <Spacer height={8} />
                    <Row align="space-between" style={{ alignItems: 'flex-start' }}>
                        <MultilineContainer>
                            <Body.Med size={14}>Gas fees:</Body.Med>
                            <Body.Med
                                size={14}
                                color={cssVar('--text-tertiery', colors.medGrey)}
                                style={{ fontStyle: 'italic', maxWidth: 500 }}
                            >
                                This one's on us!
                            </Body.Med>
                        </MultilineContainer>
                        <Body.Med size={14}>FREE</Body.Med>
                    </Row>
                    <Spacer height={8} />
                    <Row align="space-between" style={{ alignItems: 'flex-start' }}>
                        <Body.Med size={14}>You pay:</Body.Med>
                        <Body.Bold size={14}>{total}</Body.Bold>
                    </Row>
                    <Spacer height={16} />
                </BreakdownContainer>
            </Collapsible>
            <Spacer height={8} />
            <Spacer height={4} />
            <Row align="space-between">
                <Body.Med size={16}>Pay with:</Body.Med>
                <Row>
                    <Logo src={usdcLogo} />
                    <Spacer width={4} />
                    <Body.SmBold size={16}>USDC</Body.SmBold>
                </Row>
            </Row>
            <Spacer height={8} />
            <Spacer height={4} />
            <Row align="space-between">
                <Body.Med size={16}>Network:</Body.Med>
                <Row>
                    <NetworkLogo network={Network.Polygon} />
                    <Spacer width={4} />
                    <Body.SmBold size={16}>Polygon</Body.SmBold>
                </Row>
            </Row>
            <Spacer height={8} />
            <Spacer height={4} />
            <Row align="space-between">
                <Body.Med size={16}>From wallet:</Body.Med>
                <Body.SmBold size={16}>{wallet}</Body.SmBold>
            </Row>
            <Spacer height={8} />
            <Spacer height={4} />
            <Row align="space-between">
                <Body.Med size={16}>Payment date:</Body.Med>
                <Body.SmBold size={16}>{`On the ${ordinalDay} of every month`}</Body.SmBold>
            </Row>
            <Spacer height={8} />
            <Spacer height={4} />
            <Row align="space-between">
                <Body.Med size={16}>Payments duration:</Body.Med>
                <Body.SmBold size={16}>{`${
                    totalPayments === 0 ? 'Unlimited' : totalPayments
                } month${totalPayments === 1 ? '' : 's'}`}</Body.SmBold>
            </Row>
            <Spacer height={8} />
            <Spacer height={4} />
            <Row align="space-between">
                <Body.Med size={16}>First payment:</Body.Med>
                <Body.SmBold size={16}>{firstPaymentDate}</Body.SmBold>
            </Row>
        </>
    )
}
